import { useEffect, useRef, useState, useCallback } from 'react';

const FRAME_COUNT = 32;
const AUTO_ROTATE_MS = 100; // ~10 FPS — smooth but gentle showcase spin
const DRAG_THRESHOLD = 4;
const RESUME_DELAY = 3000;
const INERTIA_FRICTION = 0.92;
const INERTIA_MIN_VELOCITY = 0.3;

const BASE_URL =
  'https://cdn.imagin.studio/getImage?customer=wegems-supplier&make=toyota&modelFamily=landcruiser&modelYear=2024&paintId=pspc0004&width=1200&angle=';

function getFrameUrl(i: number) {
  return `${BASE_URL}${200 + i}`;
}

export default function CarSpin() {
  const [loaded, setLoaded] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);


  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgsRef = useRef<(HTMLImageElement | null)[]>([]);
  const frameRef = useRef(0);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const lastMoveTime = useRef(0);
  const velocity = useRef(0);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inertiaRaf = useRef<number | null>(null);
  const inertiaAccum = useRef(0);
  const autoRaf = useRef<number | null>(null);
  const autoRunning = useRef(false);

  // Direct DOM frame swap — loop ALL images (imagin.studio pattern, self-healing)
  const showFrame = useCallback((index: number) => {
    if (frameRef.current === index) return;
    const imgs = imgsRef.current;
    for (let i = 0; i < imgs.length; i++) {
      imgs[i]?.classList.toggle('current', i === index);
    }
    frameRef.current = index;
  }, []);

  // Preload tracking (only state needed for loader UI)
  const onImgLoad = useCallback(() => {
    setLoaded((n) => {
      const next = n + 1;
      if (next === FRAME_COUNT) setAllLoaded(true);
      return next;
    });
  }, []);

  // Show first frame once it loads (sizer pattern handles dimensions naturally)
  const onFirstLoad = useCallback(() => {
    const first = imgsRef.current[0];
    if (first) {
      first.classList.add('current');
    }
  }, []);

  // rAF-based auto-rotate — frame-aligned, no timing jitter
  const startAuto = useCallback(() => {
    if (autoRunning.current) return;
    autoRunning.current = true;
    let lastTime = 0;
    const tick = (now: number) => {
      if (!autoRunning.current) return;
      if (!lastTime) lastTime = now;
      const elapsed = now - lastTime;
      if (elapsed >= AUTO_ROTATE_MS) {
        showFrame((frameRef.current + 1) % FRAME_COUNT);
        lastTime = now;
      }
      autoRaf.current = requestAnimationFrame(tick);
    };
    autoRaf.current = requestAnimationFrame(tick);
  }, [showFrame]);

  const stopAuto = useCallback(() => {
    autoRunning.current = false;
    if (autoRaf.current) {
      cancelAnimationFrame(autoRaf.current);
      autoRaf.current = null;
    }
  }, []);

  // Start auto-rotate when all frames loaded
  useEffect(() => {
    if (!allLoaded) return;
    startAuto();
    return stopAuto;
  }, [allLoaded, startAuto, stopAuto]);

  const stopInertia = useCallback(() => {
    if (inertiaRaf.current) {
      cancelAnimationFrame(inertiaRaf.current);
      inertiaRaf.current = null;
    }
  }, []);

  const startResume = useCallback(() => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      startAuto();
    }, RESUME_DELAY);
  }, [startAuto]);

  // Inertia animation on pointer up — fractional accumulation for smooth decay
  const startInertia = useCallback(() => {
    inertiaAccum.current = 0;
    const tick = () => {
      velocity.current *= INERTIA_FRICTION;
      if (Math.abs(velocity.current) < INERTIA_MIN_VELOCITY) {
        inertiaRaf.current = null;
        startResume();
        return;
      }
      const dir = velocity.current > 0 ? 1 : -1;
      inertiaAccum.current += Math.abs(velocity.current);
      const steps = Math.floor(inertiaAccum.current);
      if (steps > 0) {
        inertiaAccum.current -= steps;
        showFrame((frameRef.current + dir * steps + FRAME_COUNT) % FRAME_COUNT);
      }
      inertiaRaf.current = requestAnimationFrame(tick);
    };
    inertiaRaf.current = requestAnimationFrame(tick);
  }, [showFrame, startResume]);

  // Pointer events
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      dragging.current = true;
      lastX.current = e.clientX;
      lastMoveTime.current = performance.now();
      velocity.current = 0;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      stopAuto();
      stopInertia();
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    },
    [stopAuto, stopInertia]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - lastX.current;
      const steps = Math.floor(Math.abs(dx) / DRAG_THRESHOLD);
      if (steps > 0) {
        const dir = dx > 0 ? 1 : -1;
        showFrame((frameRef.current + dir * steps + FRAME_COUNT) % FRAME_COUNT);

        const now = performance.now();
        const dt = now - lastMoveTime.current;
        if (dt > 0) velocity.current = dx / dt;
        lastMoveTime.current = now;
        lastX.current += dir * steps * DRAG_THRESHOLD;
      }
    },
    [showFrame]
  );

  const onPointerUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    if (Math.abs(velocity.current) > 0.5) {
      startInertia();
    } else {
      startResume();
    }
  }, [startInertia, startResume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      autoRunning.current = false;
      if (autoRaf.current) cancelAnimationFrame(autoRaf.current);
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      if (inertiaRaf.current) cancelAnimationFrame(inertiaRaf.current);
    };
  }, []);

  const pct = Math.round((loaded / FRAME_COUNT) * 100);

  return (
    <div
      ref={wrapperRef}
      className="spin-wrapper anim-fade-up delay-2"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="spin-canvas">
        {/* All 32 frames pre-rendered in DOM — frame swap via visibility toggle */}
        {Array.from({ length: FRAME_COUNT }, (_, i) => (
          <img
            key={i}
            ref={(el) => { imgsRef.current[i] = el; }}
            src={getFrameUrl(i)}
            alt={i === 0 ? 'Toyota Land Cruiser 360°' : ''}
            draggable={false}
            onLoad={i === 0 ? () => { onFirstLoad(); onImgLoad(); } : onImgLoad}
          />
        ))}

        {!allLoaded && (
          <div className="spin-loader">
            <div className="spin-loader-bar">
              <div className="spin-loader-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="spin-loader-text">{pct}%</span>
          </div>
        )}
      </div>
    </div>
  );
}
